import React, { createContext, useCallback, useMemo, useRef, useState } from "react";

import { useAnalytics } from "utils";
import { useSignTOS } from "api/tos";

export const TOSContext = createContext(null);

/**
 * TOSProvider
 * @description Mounted once, app-wide, in `PostAuthProviders` - inside `QueryClientProvider`,
 * since it owns the sign mutation. It is ambient and gates nothing on its own: gating requires a
 * surface to opt in via `useTOSGate({ enabled: true })`.
 *
 * It holds the `agreed` checkbox state, the ref behind `requestConsentAttention()`, and the single
 * `useSignTOS()` instance - mutation state, unlike the query cache, is per-hook-instance, so a
 * per-consumer hook would leave the consent block reporting no error for a host's failed sign.
 */
export const TOSProvider = ({ children }) => {
  // This provider is app-wide and some test harnesses mount it without an AnalyticsProvider, so
  // tracking is best-effort rather than a hard dependency.
  const analytics = useAnalytics();
  const [agreed, setAgreed] = useState(false);
  const consentNode = useRef(null);
  const inFlight = useRef(null);
  const gatedHosts = useRef(0);

  const { mutateAsync, error: signError, isPending: signing, reset: resetSign } = useSignTOS();

  // Through a ref so `registerGate` below can have a stable identity for the lifetime of the
  // provider. react-query rebuilds its result object every render, so a `reset` in the useCallback
  // deps would hand every gate a new register function on some renders - and since unregistering
  // drops the host count to zero, that would untick the box mid-screen.
  const resetSignRef = useRef(resetSign);
  resetSignRef.current = resetSign;

  /**
   * Registers a gated host (every `useTOSGate({ enabled: true })` consumer) and returns its
   * unregister. When the LAST one goes away the screen is gone, so the tick and any sign error go
   * with it.
   *
   * Host count, not the consent block's own unmount: `agreed` is app-wide and hosts re-render
   * without their block routinely, so keying off the block unticked a box the user had already
   * ticked. A route switch still clears, since React runs every unmount cleanup for a commit
   * before the incoming tree's effects.
   */
  const registerGate = useCallback(() => {
    gatedHosts.current += 1;
    return () => {
      gatedHosts.current -= 1;
      if (gatedHosts.current > 0) return;
      setAgreed(false);
      // react-query only clears a mutation's error on the next `mutate`, and this provider never
      // unmounts - so without this a failed sign on Checkout renders under the freshly-unticked
      // checkbox on /addpm, for an action the user has not taken there.
      resetSignRef.current();
    };
  }, []);

  /**
   * Registers the consent block's DOM node. `TOSConsent` clears it on unmount by registering
   * `null`: a stale ref to an unmounted node makes a blocked click silently do nothing, which is
   * the exact failure this callback exists to prevent.
   */
  const registerConsentNode = useCallback((node) => {
    consentNode.current = node ?? null;
  }, []);

  const requestConsentAttention = useCallback(() => {
    consentNode.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, []);

  const signTOS = useCallback(async () => {
    // Concurrent calls share one POST. The cache short-circuit in useSignTOS makes signing
    // idempotent across *sequential* calls, but two fast clicks - or a host and a sibling in the
    // same tick - both read a cache that nothing has written yet and would both sign. Hosts still
    // check `signing` to drive their loading state; this is what makes the double POST impossible
    // rather than merely unlikely.
    if (inFlight.current) return inFlight.current;

    const attempt = (async () => {
      try {
        const result = await mutateAsync();
        // Only when a POST actually happened. Callers await this unconditionally by design, and
        // the already-signed majority hits useSignTOS's cache short-circuit - tracking on every
        // resolve would make this event measure "pressed a CTA" rather than "consented".
        if (result?.posted) analytics?.track?.("TOS_JIT_Signed");
        return result;
      } catch (error) {
        analytics?.track?.("TOS_JIT_Sign_Failed");
        // Rethrow: hosts await this at the top of their CTA handler and must abort on failure.
        throw error;
      } finally {
        // Cleared either way, so a failed sign can be retried
        inFlight.current = null;
      }
    })();

    inFlight.current = attempt;
    return attempt;
  }, [mutateAsync, analytics]);

  const value = useMemo(
    () => ({
      agreed,
      setAgreed,
      signTOS,
      signError: signError ?? null,
      signing,
      registerConsentNode,
      registerGate,
      requestConsentAttention,
    }),
    [
      agreed,
      signTOS,
      signError,
      signing,
      registerConsentNode,
      registerGate,
      requestConsentAttention,
    ],
  );

  return <TOSContext.Provider value={value}>{children}</TOSContext.Provider>;
};

export default TOSProvider;
